import { useState, useEffect } from 'react';
import { Plus, Trash2, Edit2, AlertCircle, CheckCircle, Loader } from 'lucide-react';
import { API_BASE, getAuthHeaders } from '../../services/apiClient';
import { ModuleGroupingHelpButton } from '../modals/ModuleGroupingHelpModal';

interface ModuleGrouping {
  id: number;
  group_name: string;
  members: string[];
  created_at: string;
}

interface CreateGroupingRequest {
  group_name: string;
  members: string[];
}

export default function ModuleGroupingEditor() {
  const [groupings, setGroupings] = useState<ModuleGrouping[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formData, setFormData] = useState({
    group_name: '',
    members: '',
  });

  useEffect(() => {
    loadGroupings();
  }, []);

  const loadGroupings = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${API_BASE}/module-groupings`, {
        headers: await getAuthHeaders(),
      });
      if (!response.ok) throw new Error('Failed to load groupings');
      const data = await response.json();
      setGroupings(data.groupings || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    if (!formData.group_name.trim()) {
      setError('Group name is required');
      return;
    }

    const members = formData.members
      .split(',')
      .map(m => m.trim())
      .filter(m => m.length > 0);

    if (members.length === 0) {
      setError('At least one module is required');
      return;
    }

    try {
      if (editingId) {
        // Update
        const response = await fetch(`${API_BASE}/module-groupings/${editingId}`, {
          method: 'PUT',
          headers: await getAuthHeaders(),
          body: JSON.stringify({ members }),
        });
        if (!response.ok) throw new Error('Failed to update grouping');
        setSuccessMessage('Grouping updated successfully');
      } else {
        // Create
        const response = await fetch(`${API_BASE}/module-groupings`, {
          method: 'POST',
          headers: await getAuthHeaders(),
          body: JSON.stringify({ group_name: formData.group_name, members }),
        });
        if (response.status === 409) {
          setError('Group name already exists');
          return;
        }
        if (!response.ok) throw new Error('Failed to create grouping');
        setSuccessMessage('Grouping created successfully');
      }

      setFormData({ group_name: '', members: '' });
      setEditingId(null);
      setShowForm(false);
      await loadGroupings();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Are you sure you want to delete this grouping?')) return;

    setError('');
    setSuccessMessage('');
    try {
      const response = await fetch(`${API_BASE}/module-groupings/${id}`, {
        method: 'DELETE',
        headers: await getAuthHeaders(),
      });
      if (!response.ok) throw new Error('Failed to delete grouping');
      setSuccessMessage('Grouping deleted successfully');
      await loadGroupings();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    }
  };

  const handleEdit = (grouping: ModuleGrouping) => {
    setFormData({
      group_name: grouping.group_name,
      members: grouping.members.join(', '),
    });
    setEditingId(grouping.id);
    setShowForm(true);
  };

  const handleCancel = () => {
    setFormData({ group_name: '', members: '' });
    setEditingId(null);
    setShowForm(false);
    setError('');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Module Groupings</h2>
          <p className="text-sm text-gray-500 mt-1">
            Group modules by synonym/alias to expand detection results. When one module is detected, all grouped modules are included.
          </p>
        </div>
        {!showForm && (
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus size={18} />
            Add Grouping
          </button>
        )}
      </div>

      {/* Messages */}
      {error && (
        <div className="flex gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
          <AlertCircle size={20} className="text-red-600 flex-shrink-0" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}
      {successMessage && (
        <div className="flex gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
          <CheckCircle size={20} className="text-green-600 flex-shrink-0" />
          <p className="text-sm text-green-700">{successMessage}</p>
        </div>
      )}

      {/* Form */}
      {showForm && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            {editingId ? 'Edit Grouping' : 'Create New Grouping'}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                Group Name
                <ModuleGroupingHelpButton />
              </label>
              <input
                type="text"
                value={formData.group_name}
                onChange={e => setFormData({ ...formData, group_name: e.target.value })}
                disabled={editingId !== null}
                placeholder="e.g., Rate Request Family"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                Module Names (comma-separated)
                <ModuleGroupingHelpButton />
              </label>
              <textarea
                value={formData.members}
                onChange={e => setFormData({ ...formData, members: e.target.value })}
                placeholder="Rate Request, Special Rate Request, RR, SRR"
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
              />
              <p className="text-xs text-gray-500 mt-1">Separate module names with commas</p>
            </div>

            <div className="flex gap-2">
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                {editingId ? 'Update' : 'Create'}
              </button>
              <button
                type="button"
                onClick={handleCancel}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Groupings List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader size={24} className="animate-spin text-blue-600" />
        </div>
      ) : groupings.length === 0 ? (
        <div className="text-center py-12 bg-white border border-gray-200 rounded-lg">
          <p className="text-gray-500">No module groupings yet</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {groupings.map(grouping => (
            <div key={grouping.id} className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{grouping.group_name}</h3>
                  <p className="text-xs text-gray-500 mt-1">
                    Created: {new Date(grouping.created_at).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleEdit(grouping)}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                  >
                    <Edit2 size={18} />
                  </button>
                  <button
                    onClick={() => handleDelete(grouping.id)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-700">Members:</p>
                <div className="flex flex-wrap gap-2">
                  {grouping.members.map(member => (
                    <span
                      key={member}
                      className="inline-block px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm border border-blue-200"
                    >
                      {member}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
